/**
 * Vike Integration Helpers for SiteTheme
 * Framework-level functions to be called from project-specific Vike files
 */

import { api } from 'aldoo-ra/API'
import { getAvailableThemes } from './index.js'

const siteThemeAPI = api("site-theme")

/**
 * Helper function to select theme based on user preference during SSR
 * Limited server-side detection - client-side detection is more reliable
 * @param {Object} themeData - Theme data from loadThemeDataForSSR
 * @param {Object} pageContext - Vike page context with request headers
 * @returns {Object} Selected theme object
 */
function selectThemeBasedOnUserPreference(themeData, pageContext) {
  // Default to site active theme
  let selectedTheme = themeData.siteActiveTheme

  // Check for user theme preference in cookies
  const cookieHeader = pageContext?.headers?.cookie || ''
  const userThemeMatch = cookieHeader.match(/ui\.theme\.userPreference=([^;]+)/)
  if (userThemeMatch) {
    const preferredThemeId = decodeURIComponent(userThemeMatch[1])
    const preferredTheme = themeData.availableThemes?.find(theme => theme._id === preferredThemeId)
    if (preferredTheme) {
      return preferredTheme
    }
  }

  // For production builds, don't attempt server-side theme detection
  // Let client-side detection handle browser preferences
  return selectedTheme
}

/**
 * Load theme data for SSR - Framework function
 * Call this from your project's +data.js or +onBeforeRender.js
 */
export async function loadThemeDataForSSR(themePreviewId = null, pageContext = null) {
  try {

    // Helper function to normalize isActive field to array format
    const normalizeIsActive = (isActive) => {
      if (Array.isArray(isActive)) return isActive
      return [] // Any other value = inactive
    }

    // Check if browser theme auto-adaptation is enabled via ParamIO
    let isAutoAdaptEnabled = false
    try {
      const paramIOAPI = api("param-io")
      const paramResponse = await paramIOAPI({
        get: {
          key: 'SITE_THEME_AUTO_ADAPT'
        }
      })
      isAutoAdaptEnabled = paramResponse.result === true || paramResponse.result === 'true'
    } catch (error) {
    }

    // Get all themes using the unified SiteTheme API
    const availableThemes = await getAvailableThemes()

    // Find active themes for both contexts
    let siteActiveTheme = null
    let adminActiveTheme = null

    // Find active themes for both contexts from the available themes
    for (const theme of availableThemes) {
      const activeContexts = normalizeIsActive(theme.isActive)

      if (activeContexts.includes('site')) {
        siteActiveTheme = theme
      }
      if (activeContexts.includes('admin')) {
        adminActiveTheme = theme
      }
    }

    // Handle theme preview override
    let previewTheme = null
    if (themePreviewId) {
      previewTheme = availableThemes.find(theme => theme._id === themePreviewId)
      if (previewTheme) {
        // Override site active theme with preview theme
        siteActiveTheme = previewTheme
      }
    }

    // Find default theme as fallback
    const defaultTheme = availableThemes.find(theme => theme.isDefault)

    // Use default theme if no active themes found - LOG THIS AS ERROR
    if (!siteActiveTheme && defaultTheme) {
      siteActiveTheme = defaultTheme
    }
    if (!adminActiveTheme && defaultTheme) {
      adminActiveTheme = defaultTheme
    } else if (siteActiveTheme) {
      // Logic for fallback if only siteActiveTheme exists?
      // Current code doesn't set adminActiveTheme here, which is fine (stays null)
    }

    if (adminActiveTheme) {
      const adminColors = adminActiveTheme.colors || [];
      const adminKeys = Array.isArray(adminColors)
        ? adminColors.map(c => c.key).filter(k => k.startsWith('admin_'))
        : Object.keys(adminColors).filter(k => k.startsWith('admin_'));
    }

    // Apply user theme preferences for SSR consistency (limited server-side detection)
    // This ensures the theme data passed to client matches the CSS generated
    if (pageContext && siteActiveTheme && availableThemes.length > 0) {
      const userPreferredTheme = selectThemeBasedOnUserPreference(
        { siteActiveTheme, availableThemes },
        pageContext
      )
      if (userPreferredTheme && userPreferredTheme._id !== siteActiveTheme._id) {
        siteActiveTheme = userPreferredTheme
      }
    }

    return {
      activeTheme: siteActiveTheme,
      siteActiveTheme,
      adminActiveTheme,
      availableThemes,
      isPreview: !!previewTheme,
      previewTheme,
      isAutoAdaptEnabled, // Flag for client-side theme system
      timestamp: Date.now() // Cache invalidation
    }

  } catch (error) {
    return {
      activeTheme: null,
      siteActiveTheme: null,
      adminActiveTheme: null,
      availableThemes: [],
      isPreview: false,
      previewTheme: null,
      isAutoAdaptEnabled: false,
      timestamp: Date.now()
    }
  }
}

/**
 * Generate CMS Color CSS for SSR - Framework function
 * Call this from your project's +data.js
 */
export async function generateCMSColorCSS(pageContext) {
  try {
    // Check for theme preview parameter
    const urlSearchParams = new URLSearchParams(pageContext.urlParsed.search)
    const themePreviewId = urlSearchParams.get('theme_preview')

    // Load theme data
    const themeData = await loadThemeDataForSSR(themePreviewId, pageContext)

    // Generate CMS color utilities CSS - Use ADMIN theme for admin pages, SITE theme for site pages
    let cmsColorCSS = null
    let themeForCSS = null

    // Determine which theme to use for CSS generation based on URL
    const isAdminPage = pageContext.urlParsed.pathname.startsWith('/admin')
    const currentContext = isAdminPage ? 'admin' : 'site'

    // Check for user's theme preference from cookie
    const cookieHeader = pageContext.headers?.cookie || ''
    const themeIdMatch = cookieHeader.match(new RegExp(`themeId_${currentContext}=([^;]+)`))
    const userThemeId = themeIdMatch ? themeIdMatch[1] : null

    if (themePreviewId && themeData?.previewTheme) {
      themeForCSS = themeData.previewTheme
    } else if (userThemeId && themeData?.availableThemes) {
      // Use user's preferred theme from cookie if available
      themeForCSS = themeData.availableThemes.find(t => t._id === userThemeId)
      if (!themeForCSS) {
        // Fallback to default theme for context if user's theme not found
        themeForCSS = isAdminPage ? themeData.adminActiveTheme : themeData.siteActiveTheme
      }
    } else if (isAdminPage && themeData?.adminActiveTheme?._id) {
      themeForCSS = themeData.adminActiveTheme
    } else if (themeData?.siteActiveTheme?._id) {
      themeForCSS = themeData.siteActiveTheme
    }

    // Skip CSS generation for the default theme — it relies on CSS variable defaults in Layout.css.
    // The server would generate CSS from the theme's old top-level color props (wrong values)
    // which would override Tailwind's CSS variable classes with incorrect colors.
    if (themeForCSS?._id && !themeForCSS?.isDefault) {
      try {
        const response = await siteThemeAPI({
          get_cms_color_stylesheet: {
            themeId: themeForCSS._id
          }
        })

        if (response.result) {
          cmsColorCSS = {
            css: response.result.css,
            theme: response.result.theme,
            colors: response.result.colors,
            generated: response.result.generated,
            themeId: themeForCSS._id // Always include theme ID for attribute setting
          }
        }
      } catch (error) {
        console.error('Error generating CSS:', error)
      }
    }

    return {
      themeData,
      isThemePreview: !!themePreviewId,
      cmsColorCSS
    }
  } catch (error) {
    console.error('Error in generateCMSColorCSS:', error)
    return {
      themeData: null,
      isThemePreview: false,
      cmsColorCSS: null
    }
  }
}

/**
 * Create Head JSX for theme injection - Framework function
 * Call this from your project's +Head.jsx
 */
export function createThemeHeadElements(data) {
  const themeData = data?.themeData
  const cmsColorCSS = data?.cmsColorCSS

  const elements = []

  // Add theme initialization script
  if (themeData) {
    elements.push({
      type: 'script',
      key: 'theme-init',
      props: {
        dangerouslySetInnerHTML: {
          __html: require('./index.js').createThemeInitScript(themeData)
        }
      }
    })
  }

  // Add CMS color utilities CSS
  if (cmsColorCSS?.css) {
    elements.push({
      type: 'style',
      key: 'cms-color-utilities',
      props: {
        id: 'cms-color-utilities',
        'data-theme-id': cmsColorCSS.theme?._id || cmsColorCSS.theme?.id,
        dangerouslySetInnerHTML: { __html: cmsColorCSS.css }
      }
    })
  }

  return elements
}